import { Component, EventEmitter, Input, Output } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import type { ResourceTableService } from 'libs/ng-ui/src/lib/_services/resource-table.service';
import {
  convertCamelCaseToTitleCase,
  environment,
  getResourceDisplayName,
  projectEnv,
} from 'utils';
import { isIntercomVisible } from '../../../_utils/intercom';
import { CtxButtonComponent } from '../../button/button.component';
import { NgClass } from '@angular/common';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { HlmIconComponent } from '../../icon';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'table-paginator',
  templateUrl: './paginator.component.html',
  standalone: true,
  imports: [
    MatFormFieldModule,
    HlmIconComponent,
    MatSelectModule,
    MatOptionModule,
    NgClass,
    CtxButtonComponent,
  ],
})
export class CtxTablePaginatorComponent {
  convertCamelCaseToTitleCase = convertCamelCaseToTitleCase;

  projectName = projectEnv.get('projectName');

  get isIntercomVisible(): boolean {
    return isIntercomVisible(environment.get('name'), this.projectName);
  }

  // Icons

  @Input() resourceTableService: ResourceTableService;

  /** Whether to hide the page size selection UI from the user. */
  @Input() hidePageSize: boolean;

  /** Whether to show the first/last buttons UI to the user. */
  @Input() showFirstLastButtons: boolean;

  @Input() tableContentRef: HTMLElement;

  /** Event emitted when the paginator changes the page size or page index. */
  @Output() readonly page: EventEmitter<PageEvent> =
    new EventEmitter<PageEvent>();

  /** Resets the scroll position. */
  _resetScroll = () => {
    this.tableContentRef.scrollTo(0, 0);
  };
  /** Advances to the next page if it exists. */
  async nextPage() {
    if (!this.hasNextPage) {
      return;
    }
    this._resetScroll();
    this.resourceTableService.tablePageNumber =
      this.resourceTableService.tablePageNumber + 1;
    await this.resourceTableService.tableList();
  }

  /** Move back to the previous page if it exists. */
  async previousPage() {
    if (!this.hasPreviousPage) {
      return;
    }
    this._resetScroll();

    this.resourceTableService.tablePageNumber =
      this.resourceTableService.tablePageNumber - 1;

    await this.resourceTableService.tableList();
  }

  /** Move to the first page if not already there. */
  async firstPage() {
    // hasPreviousPage being false implies at the start
    if (!this.hasPreviousPage) {
      return;
    }
    this._resetScroll();

    this.resourceTableService.tablePageNumber = 1;
    await this.resourceTableService.tableList();
  }

  /** Move to the last page if not already there. */
  async lastPage() {
    // hasNextPage being false implies at the end
    if (!this.hasNextPage) {
      return;
    }
    this._resetScroll();
    this.resourceTableService.tablePageNumber = this.numberOfPages;

    await this.resourceTableService.tableList();
  }

  /** Whether there is a previous page. */
  get hasPreviousPage(): boolean {
    return (
      this.resourceTableService.tablePageNumber > 1 &&
      this.resourceTableService.tablePageSize !== 0
    );
  }

  /** Whether there is a next page. */
  get hasNextPage(): boolean {
    const maxPageIndex = this.numberOfPages;
    return (
      this.resourceTableService.tablePageNumber < maxPageIndex &&
      this.resourceTableService.tablePageSize !== 0
    );
  }

  /** Calculate the number of pages */
  get numberOfPages(): number {
    if (!this.resourceTableService.tablePageSize) {
      return 0;
    }

    return Math.ceil(
      this.resourceTableService.tableRowCount /
        this.resourceTableService.tablePageSize
    );
  }

  /** Checks whether the buttons for going forwards should be disabled. */
  get nextButtonsDisabled() {
    return !this.hasNextPage;
  }

  /** Checks whether the buttons for going backwards should be disabled. */
  get previousButtonsDisabled() {
    return !this.hasPreviousPage;
  }

  get rangeLabel() {
    const pageSize = this.resourceTableService.tablePageSize;
    const rowCount = this.resourceTableService.tableRowCount;
    const pageNumber = this.resourceTableService.tablePageNumber;

    if (rowCount === 0 || pageSize === 0) {
      return `0 of ${rowCount}`;
    }

    const startIndex = (pageNumber - 1) * pageSize;

    // If the start index exceeds the list length, do not try and fix the end index to the end.
    const endIndex =
      startIndex < rowCount
        ? Math.min(startIndex + pageSize, rowCount)
        : startIndex + pageSize;

    return `${startIndex} – ${endIndex} of ${Math.max(rowCount, 0)}`;
  }

  getResourceDisplayName = getResourceDisplayName;

  async handlePageSizeChange(event: MatSelectChange) {
    this.resourceTableService.tablePageSize = event.value;
    await this.resourceTableService.tableList();
    this.resourceTableService.saveTablePreferences();
  }

  get sortedColumns() {
    return this.resourceTableService.columns.sort((a, b) => {
      const _aDisplayName = getResourceDisplayName(
        a.property,
        this.projectName
      );
      const _bDisplayName = getResourceDisplayName(
        b.property,
        this.projectName
      );

      if (_aDisplayName < _bDisplayName) {
        return -1;
      } else if (_aDisplayName > _bDisplayName) {
        return 1;
      } else {
        return 0;
      }
    });
  }
}
